import React from 'react';

function PrivacyPolicy() {
  const handleBack = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span className="mr-2">←</span>
              Back
            </button>
          </div>

          <h1 className="text-4xl font-bold text-white mb-8 text-center">
            Privacy Policy
          </h1>
          
          <div className="text-gray-300 space-y-6">
            <p className="text-sm text-gray-400 mb-8">
              <strong>Last Updated:</strong> January 8, 2025
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">Personal Information</h3>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>Account Information:</strong> Email address, password (encrypted), user preferences</li>
                <li><strong>Trading Data:</strong> API keys (encrypted), trading history, portfolio data</li>
                <li><strong>Payment Information:</strong> Processed by Stripe (we don't store card details)</li>
                <li><strong>Communication:</strong> Support emails, feedback, correspondence</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">Technical Information</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Usage Data:</strong> Login times, feature usage, trading activity</li>
                <li><strong>Device Information:</strong> IP address, browser type, device identifiers</li>
                <li><strong>Cookies:</strong> Session management, preferences, analytics</li>
                <li><strong>Log Data:</strong> System logs, error reports, performance metrics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              
              <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-blue-100 mb-2">Primary Uses</h3>
                <ul className="list-disc list-inside space-y-2 text-blue-100">
                  <li>Provide and maintain the trading bot service</li>
                  <li>Execute trades through your connected broker accounts</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Send service-related communications and updates</li>
                  <li>Provide customer support and technical assistance</li>
                  <li>Improve our algorithms and service quality</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">Analytics and Improvement</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Analyze trading patterns to improve AI algorithms</li>
                <li>Monitor system performance and reliability</li>
                <li>Aggregate anonymous statistics for service optimization</li>
                <li>Detect and prevent fraud or security threats</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Information Sharing</h2>
              
              <div className="bg-green-900 border border-green-700 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-green-100 mb-2">We DO NOT sell your personal data</h3>
                <p className="text-green-100">
                  actuvo.ai does not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">Necessary Sharing</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Exchange APIs:</strong> Trading data shared with Hyperliquid for trade execution</li>
                <li><strong>Payment Processing:</strong> Payment information processed by Stripe</li>
                <li><strong>Email Services:</strong> Contact information used by SendGrid for service emails</li>
                <li><strong>Legal Requirements:</strong> When required by law or court order</li>
                <li><strong>Service Providers:</strong> Trusted vendors who assist in service delivery</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Data Security</h2>
              
              <div className="bg-purple-900 border border-purple-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-100 mb-3">Security Measures</h3>
                <ul className="list-disc list-inside space-y-2 text-purple-100">
                  <li><strong>Encryption:</strong> All sensitive data encrypted at rest and in transit</li>
                  <li><strong>API Key Security:</strong> Trading API keys encrypted with industry-standard methods</li>
                  <li><strong>Authentication:</strong> JWT tokens with expiration for secure access</li>
                  <li><strong>Database Security:</strong> Protected MongoDB with access controls</li>
                  <li><strong>Network Security:</strong> HTTPS everywhere, secure server infrastructure</li>
                  <li><strong>Regular Updates:</strong> Security patches and system updates applied promptly</li>
                </ul>
              </div>

              <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg mt-4">
                <h3 className="text-lg font-semibold text-blue-100 mb-3">4.1 API Key Security (Hyperliquid API Wallet)</h3>
                <p className="text-blue-100 mb-3">
                  The Hyperliquid API Wallet private key you paste into actuvo is the most sensitive
                  piece of data we handle. Specifically:
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-100">
                  <li>API Wallet keys are <strong>encrypted at rest</strong> using Fernet (AES-128 in CBC mode with HMAC) before they ever touch the database.</li>
                  <li>The decryption key is held only in server-side environment variables and is <strong>never returned to your browser</strong>.</li>
                  <li>API Wallet keys are <strong>never written to application logs</strong>, error reports, or analytics.</li>
                  <li>After you save a key it is <strong>not shown again</strong> — only a masked preview (e.g. <code>0x1234••••••5678</code>) is displayed.</li>
                  <li>You can <strong>delete or replace</strong> your API Wallet key from the Settings page at any time.</li>
                  <li>If you suspect a key is compromised, <strong>revoke the API Wallet directly on Hyperliquid</strong> (More → API → Revoke). That immediately and permanently invalidates the key, regardless of what we hold.</li>
                  <li>actuvo <strong>never asks for and never accepts</strong> seed phrases, recovery phrases, or main-wallet private keys. Only Hyperliquid API Wallet (Agent Wallet) trade-only keys are supported.</li>
                  <li>API Wallets cannot withdraw funds — withdrawal authority remains exclusively with your main Hyperliquid wallet, which actuvo never receives.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Your Rights (GDPR Compliance)</h2>
              
              <p className="mb-4">If you are in the EU, you have the following rights:</p>
              
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
              </ul>

              <p className="mt-4">
                To exercise these rights, contact us at <strong>privacy@actuvo.ai</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention</h2>
              
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Account Data:</strong> Retained while your account is active</li>
                <li><strong>Trading History:</strong> Kept for 7 years for regulatory compliance</li>
                <li><strong>Payment Records:</strong> Retained per financial regulations</li>
                <li><strong>Support Communications:</strong> Kept for 2 years</li>
                <li><strong>Inactive Accounts:</strong> Automatically deleted after 90 days of inactivity</li>
                <li><strong>Log Data:</strong> Automatically deleted after 30 days</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Cookies and Tracking</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">Types of Cookies</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic functionality and security</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the service</li>
                <li><strong>Session Cookies:</strong> Temporary cookies deleted when you close your browser</li>
              </ul>

              <p className="mt-4">
                You can control cookies through your browser settings, but disabling essential cookies may affect service functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Third-Party Services</h2>
              
              <div className="bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-3">Integrated Services</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Hyperliquid:</strong> Exchange integration for grid trading execution</li>
                  <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                  <li><strong>SendGrid:</strong> Email delivery and communication</li>
                  <li><strong>Market Data Providers:</strong> Real-time financial data</li>
                </ul>
                <p className="mt-3 text-sm">
                  These services have their own privacy policies. We encourage you to review them.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. International Transfers</h2>
              
              <p className="mb-4">
                Your data may be processed in countries other than your own. We ensure appropriate safeguards are in place:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Standard Contractual Clauses for EU data transfers</li>
                <li>Adequacy decisions where applicable</li>
                <li>Equivalent privacy protections in all processing locations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
              
              <div className="bg-red-900 border border-red-700 p-4 rounded-lg">
                <p className="text-red-100">
                  <strong>Age Restriction:</strong> actuvo.ai is not intended for users under 18 years old. 
                  We do not knowingly collect personal information from children under 18.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to Privacy Policy</h2>
              
              <p className="mb-4">
                We may update this Privacy Policy periodically. Changes will be posted on this page with an updated date. 
                Significant changes will be communicated via email.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Us</h2>
              
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="mb-2"><strong>Privacy Questions:</strong> privacy@actuvo.ai</p>
                <p className="mb-2"><strong>Data Protection Officer:</strong> dpo@actuvo.ai</p>
                <p className="mb-2"><strong>General Support:</strong> support@actuvo.ai</p>
                <p className="mb-2"><strong>Website:</strong> https://actuvo.ai</p>
                <p className="text-sm text-gray-400 mt-3">
                  actuvo.ai is operated as a sole proprietorship. All inquiries are handled electronically via email for fastest response.
                </p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-900 border border-blue-700 rounded-lg">
              <p className="text-blue-100">
                <strong>Questions?</strong> If you have any questions about this Privacy Policy or our data practices, 
                please don't hesitate to contact us at privacy@actuvo.ai
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;